import React, { useState, useContext } from "react";
import CounselorList from "./CounselorList";
import CreateCounselorForm from "./CreateCounselorForm";
import EditCounselorForm from "./EditCounselorForm";
import { AuthContext } from "../../contexts/AuthContext";

const Counselors = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [editingCounselor, setEditingCounselor] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { user } = useContext(AuthContext);

  const handleCounselorCreated = () => {
    setActiveTab("list");
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleCounselorUpdated = () => {
    setEditingCounselor(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col">
          <h2>Counselor Management</h2>
          <p className="text-muted">
            Manage counselor information and assignments.
            {user?.is_primary && " (Primary tenant access)"}
          </p>
        </div>
      </div>

      {/* Protected Information Notice - HIPAA Compliance */}
      <div
        className="alert alert-warning d-flex align-items-center mb-4"
        role="alert"
      >
        <i className="bi bi-shield-exclamation fs-4 me-3"></i>
        <div>
          <strong>Protected Information:</strong> This section contains
          sensitive counselor data including personal contact information. All
          access and modifications are logged for compliance. Unauthorized
          access is prohibited.
        </div>
      </div>

      {/* Tabs - Bootstrap nav-tabs with green active state */}
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("list");
              setEditingCounselor(null);
            }}
          >
            <i className="bi bi-people"></i> All Counselors
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "create" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("create");
              setEditingCounselor(null);
            }}
          >
            <i className="bi bi-person-plus"></i> Create Counselor
          </button>
        </li>
      </ul>

      {/* Content */}
      <div className="tab-content mt-3">
        {/* List Mode */}
        {activeTab === "list" && !editingCounselor && (
          <>
            <h4>All Counselors</h4>
            <ProfessionalCounselorList
              onEdit={(counselor) => setEditingCounselor(counselor)}
              refreshTrigger={refreshTrigger}
            />
          </>
        )}

        {activeTab === "list" && editingCounselor && (
          <>
            <h4>Edit Counselor</h4>
            <EditCounselorForm
              counselorId={editingCounselor?.counselor_id}
              onDone={handleCounselorUpdated}
            />
          </>
        )}

        {/* Create Mode */}
        {activeTab === "create" && (
          <>
            <h4>Create Counselor</h4>
            <CreateCounselorForm onCreated={handleCounselorCreated} />
          </>
        )}
      </div>
    </div>
  );
};

// Professional Counselor List Component (Customer-Style Layout)
const ProfessionalCounselorList = ({ onEdit, refreshTrigger }) => {
  const [counselors, setCounselors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use the same hooks as before
  const { useEffect, useContext } = React;
  const { AuthContext } = require("../../contexts/AuthContext");
  const secureAxios = require("../../utils/secureAxios").default;
  const { formatPhone } = require("../../utils/formatPhone");

  const authContext = useContext(AuthContext);
  const {
    tenant_id = null,
    is_primary = false,
    loadingUser = true,
    user = null,
  } = authContext || {};

  // Load customers and counselors (same logic as before)
  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    // Load customers
    secureAxios
      .get(`/api/customers?tenant_id=${tenant_id}`)
      .then((res) => {
        if (process.env.NODE_ENV === "development") {
          console.log("✅ Customers loaded (dev only)");
        }
        setCustomers(res.data);
      })
      .catch((err) => {
        console.error("❌ Error loading customers:", err.message);
        setError(`Error loading customers: ${err.message}`);
      });
  }, [tenant_id, loadingUser, refreshTrigger]);

  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    setLoading(true);
    const params = {
      tenant_id,
      is_primary,
      ...(selectedCustomer && { customer_id: selectedCustomer }),
    };

    secureAxios
      .get("/api/counselors", { params })
      .then((res) => {
        if (process.env.NODE_ENV === "development") {
          console.log("✅ Counselors loaded (dev only)");
        }
        setCounselors(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ Error loading counselors:", err.message);
        setError(`Error loading counselors: ${err.message}`);
        setLoading(false);
      });
  }, [tenant_id, selectedCustomer, is_primary, loadingUser, refreshTrigger]);

  // Filter counselors based on search term
  const filteredCounselors = counselors.filter(
    (counselor) =>
      counselor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      counselor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!authContext) {
    return <div>AuthContext not available</div>;
  }

  if (loadingUser) {
    return <div>Loading user information...</div>;
  }

  if (!tenant_id) {
    return <div>No tenant ID available. Please check your login.</div>;
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <h5>Error:</h5>
        <p>{error}</p>
        <button
          className="btn btn-secondary"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Search Section Label */}
      <p className="text-muted mb-2">Search Counselors</p>

      {/* Search and Filter Section - Matching Customer Style */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y me-3"></i>
          </div>
        </div>

        <div className="col-md-6">
          <select
            className="form-select"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">All Customers</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Count - Matching Customer Style */}
      <div className="d-flex justify-content-end mb-3">
        <small className="text-muted">
          Showing {filteredCounselors.length} of {counselors.length} counselors
        </small>
      </div>

      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading counselors...</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Desktop Table - Matching Customer Style */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Phone</th>
                  <th>Customer</th>
                  {is_primary && <th>Organization</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCounselors.map((counselor) => (
                  <tr
                    key={`${counselor.counselor_id}-${
                      counselor.tenant_name || counselor.customer_id
                    }`}
                  >
                    <td>
                      <strong>{counselor.name}</strong>
                    </td>
                    <td>
                      <span className="text-muted">{counselor.email}</span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          counselor.status?.toLowerCase() === "active"
                            ? "bg-success"
                            : "bg-secondary"
                        }`}
                      >
                        {counselor.status || "Unknown"}
                      </span>
                    </td>
                    <td>{formatPhone(counselor.phone_number)}</td>
                    <td>{counselor.customer_name}</td>
                    {is_primary && (
                      <td>
                        <span className="badge bg-primary">
                          {counselor.tenant_name || "—"}
                        </span>
                      </td>
                    )}
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                        onClick={() => onEdit(counselor)}
                      >
                        <i className="bi bi-pencil"></i>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards - Enhanced Design */}
          <div className="d-block d-md-none">
            {filteredCounselors.map((counselor) => (
              <div
                key={`${counselor.counselor_id}-${
                  counselor.tenant_name || counselor.customer_id
                }`}
                className="card mb-3"
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="card-title mb-1">{counselor.name}</h6>
                      <p className="card-text text-muted small mb-2">
                        {counselor.email}
                      </p>

                      <div className="row text-sm">
                        <div className="col-6">
                          <strong>Phone:</strong>
                          <br />
                          <span className="text-muted">
                            {formatPhone(counselor.phone_number)}
                          </span>
                        </div>
                        <div className="col-6">
                          <strong>Customer:</strong>
                          <br />
                          <span className="text-muted">
                            {counselor.customer_name}
                          </span>
                        </div>
                      </div>

                      {is_primary && (
                        <div className="mt-2">
                          <strong>Organization:</strong>
                          <span className="badge bg-primary ms-2">
                            {counselor.tenant_name || "—"}
                          </span>
                        </div>
                      )}

                      <div className="mt-2">
                        <span
                          className={`badge ${
                            counselor.status?.toLowerCase() === "active"
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                        >
                          {counselor.status || "Unknown"}
                        </span>
                      </div>
                    </div>

                    <div className="ms-3">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onEdit(counselor)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredCounselors.length === 0 && !loading && (
            <div className="text-center py-5">
              <i className="bi bi-people display-1 text-muted"></i>
              <h4 className="mt-3">No counselors found</h4>
              <p className="text-muted">
                {searchTerm || selectedCustomer
                  ? "Try adjusting your search or filter criteria"
                  : "No counselors have been added yet"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Footer Notice - Enhanced HIPAA Compliance */}
      <div className="mt-5 p-3 bg-light rounded">
        <h6 className="text-muted">
          <i className="bi bi-shield-check"></i> Security & Compliance
        </h6>
        <small className="text-muted">
          <ul className="mb-0">
            <li>All counselor data is encrypted at rest and in transit</li>
            <li>Access to counselor information is logged and audited</li>
            <li>Session expires after 15 minutes of inactivity</li>
            <li>
              Data retention: Records are retained per HIPAA requirements
              (minimum 6 years)
            </li>
            <li>Minimum necessary access principle is enforced</li>
          </ul>
        </small>
      </div>
    </div>
  );
};

export default Counselors;
